import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { Play, Pause, RotateCcw } from "lucide-react";

const W = 400, H = 260;

const Cone = ({ x, y }) => (
  <g>
    <polygon points={`${x - 7},${y + 7} ${x + 7},${y + 7} ${x},${y - 9}`} fill="#EAB308" stroke="#111" strokeWidth="1" />
    <ellipse cx={x} cy={y + 8} rx="8" ry="2.2" fill="rgba(0,0,0,0.4)" />
  </g>
);

export default function DrillCanvas({ path = [], cones = [], label = "Drill" }) {
  const ballControls = useAnimationControls();
  const playerControls = useAnimationControls();
  const [playing, setPlaying] = useState(false);
  const mounted = useRef(true);

  const start = path[0] || { x: W / 2, y: H / 2 };
  const xs = path.map((p) => p.x);
  const ys = path.map((p) => p.y);
  const duration = Math.max(2.4, path.length * 0.75);

  const run = () => {
    if (path.length < 2) return;
    ballControls.start({
      cx: xs, cy: ys,
      transition: { duration, repeat: Infinity, repeatType: "loop", ease: "easeInOut" },
    });
    playerControls.start({
      x: xs.map((v) => v - 16), y: ys.map((v) => v - 18),
      transition: { duration, repeat: Infinity, repeatType: "loop", ease: "easeInOut" },
    });
  };

  useEffect(() => {
    mounted.current = true;
    if (playing) run();
    else { ballControls.stop(); playerControls.stop(); }
    return () => { mounted.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const reset = () => {
    setPlaying(false);
    ballControls.set({ cx: start.x, cy: start.y });
    playerControls.set({ x: start.x - 16, y: start.y - 18 });
  };

  const pathD = path.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  return (
    <div className="relative w-full" data-testid="drill-canvas">
      <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#0c1f12] shadow-[0_8px_32px_rgba(0,0,0,0.6)]">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label={`Animated drill diagram for ${label}`}>
          <defs>
            <linearGradient id="pitch" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#123d22" />
              <stop offset="100%" stopColor="#0c2a17" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill="url(#pitch)" />
          {/* mowing stripes */}
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <rect key={i} x={(W / 6) * i} y="0" width={W / 6} height={H} fill={i % 2 ? "rgba(255,255,255,0.03)" : "transparent"} />
          ))}
          {/* pitch markings */}
          <rect x="8" y="8" width={W - 16} height={H - 16} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
          <line x1={W / 2} y1="8" x2={W / 2} y2={H - 8} stroke="rgba(255,255,255,0.18)" strokeWidth="2" />
          <circle cx={W / 2} cy={H / 2} r="34" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2" />

          {/* path */}
          <path d={pathD} fill="none" stroke="#C41E24" strokeWidth="2.5" strokeDasharray="7 6" opacity="0.85" />
          {path.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r="2.4" fill="#C41E24" opacity="0.7" />
          ))}

          {/* cones */}
          {cones.map((c, i) => <Cone key={i} x={c.x} y={c.y} />)}

          {/* player marker */}
          <motion.g initial={{ x: start.x - 16, y: start.y - 18 }} animate={playerControls}>
            <circle cx="16" cy="18" r="9" fill="#111" stroke="#C41E24" strokeWidth="2.5" />
            <text x="16" y="22" textAnchor="middle" fontSize="10" fontWeight="700" fill="#fff">P</text>
          </motion.g>

          {/* ball */}
          <motion.circle initial={{ cx: start.x, cy: start.y }} animate={ballControls} r="7" fill="#fff" stroke="#111" strokeWidth="1.5" />
        </svg>

        {/* controls */}
        <div className="absolute bottom-3 left-3 flex items-center gap-2 no-print">
          <button
            data-testid="drill-play-btn"
            onClick={() => setPlaying((p) => !p)}
            className="flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {playing ? <Pause size={14} /> : <Play size={14} />}
            {playing ? "Pause" : "Play"}
          </button>
          <button
            data-testid="drill-reset-btn"
            onClick={reset}
            className="flex items-center justify-center bg-black/60 border border-white/15 hover:border-white/40 text-white p-2 rounded-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            aria-label="Reset animation"
          >
            <RotateCcw size={14} />
          </button>
        </div>
        <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-widest text-white/50 bg-black/40 px-2 py-1 rounded-sm no-print">Live Drill</span>
      </div>
    </div>
  );
}
